'use client';
import React, {useState} from 'react';

import {IconVariants} from '@/enums/Icon';
import type {SortOptions} from '@/utils/enums';

import Icon from './Icon';
import Typography from './Typography';
export enum SortOptionsType {
  A_Z = 'A-Z',
  ZERO_NINE = '0-9',
}

export enum SortOrder {
  ASCENDING = 'ASC',
  DESCENDING = 'DESC',
}
export interface ISortOption {
  name: string;
  type: SortOptionsType;
  value: SortOptions;
  sortOrder: SortOrder;
}

interface SortComponentProps {
  options: ISortOption[];
  selectedOptions: ISortOption[];
  onSortingChange: (activeOptions: ISortOption[]) => void;
}

const SortComponent: React.FC<SortComponentProps> = ({options, onSortingChange, selectedOptions = []}) => {
  const getSortOrderDisplay = (option: ISortOption) => {
    if (option.sortOrder === SortOrder.ASCENDING) {
      return option.type === SortOptionsType.A_Z ? 'A-Z' : '0-9';
    } else {
      return option.type === SortOptionsType.A_Z ? 'Z-A' : '9-0';
    }
  };
  const [activeOptions, setActiveOptions] = useState<ISortOption[]>(selectedOptions);
  const [availableOptions, setAvailableOptions] = useState<ISortOption[]>(
    selectedOptions.length > 0
      ? options.filter((option) => !selectedOptions.some((selectedOption) => selectedOption.name === option.name))
      : options,
  );

  const toggleSortOrder = (option: ISortOption) => {
    const updatedOptions = activeOptions.map((opt) => {
      if (opt.name === option.name) {
        return {...opt, sortOrder: opt.sortOrder === SortOrder.ASCENDING ? SortOrder.DESCENDING : SortOrder.ASCENDING};
      }
      return opt;
    });
    setActiveOptions(updatedOptions);
    onSortingChange(updatedOptions);
  };

  const addOption = (option: ISortOption) => {
    const newActiveOptions = [...activeOptions, option];
    setActiveOptions(newActiveOptions);
    setAvailableOptions(availableOptions.filter((opt) => opt !== option));
    onSortingChange(newActiveOptions);
  };

  const removeOption = (option: ISortOption) => {
    const newActiveOptions = activeOptions.filter((opt) => opt !== option);
    setActiveOptions(newActiveOptions);
    setAvailableOptions([...availableOptions, option]);
    onSortingChange(newActiveOptions);
  };

  const resetOptions = () => {
    setActiveOptions([]);
    setAvailableOptions(options);
    onSortingChange([]);
  };
  return (
    <div className="p relative w-[360px] cursor-pointer rounded-lg border-2 border-solid border-base-200 bg-base-color px-2 py-4">
      {/* active sorting options */}
      <div className="flex flex-col gap-[5px]">
        <div className="flex justify-between">
          <Typography className="p-2 text-sm font-bold text-base-700">Sorting by:</Typography>
          <Typography className="px-4 py-2 text-base-500" onClick={resetOptions}>
            Reset
          </Typography>
        </div>
        {activeOptions.length ? (
          activeOptions.map((option, index) => (
            <div
              key={`${option.name}${index}`}
              className={`group flex w-full items-center gap-2 rounded-none border-b border-l-4 border-b-base-100 border-l-primary bg-primary-low-contrast py-1 pl-2 font-normal enabled:hover:bg-primary-low-contrast`}>
              <div className="relative flex w-full items-center justify-between">
                <div className="flex w-full justify-between gap-2">
                  <div className="flex items-center justify-end gap-2 align-middle">
                    <span>
                      <Icon name="double-line" variant={IconVariants.LARGE} />
                    </span>
                    <Typography className={`'text-base-500' `}> {option.name} </Typography>
                  </div>
                  <div className="flex gap-2">
                    <div
                      id="sort-order"
                      className="flex items-center gap-2 rounded border border-base-300 p-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        toggleSortOrder(option);
                      }}>
                      <Icon name="sort" variant={IconVariants.SMALL} />
                      <span className="text-secondary">({getSortOrderDisplay(option)})</span>
                    </div>
                    <div className="flex items-center">
                      <span className="px-2" onClick={() => removeOption(option)}>
                        <Icon name="cross" id="remove" variant={IconVariants.MEDIUM} />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center">
            <Typography className="px-4 py-2 text-base-500">No sorting options selected</Typography>
          </div>
        )}
      </div>
      {/* available sorting options */}
      <div className="flex flex-col gap-[5px]">
        <div className="flex justify-between">
          <Typography className="p-2 text-sm font-bold uppercase text-base-700 ">Sorting Options</Typography>
        </div>
        {availableOptions.length ? (
          availableOptions.map((option) => (
            <div
              key={option.name}
              className={`group flex w-full items-center gap-2 rounded-none border-b border-b-base-100 py-1 pl-2 font-normal enabled:hover:bg-primary-low-contrast `}>
              <div className="relative flex w-full items-center justify-between">
                <div className="flex w-full justify-between gap-2" onClick={() => addOption(option)}>
                  <div className="flex items-center justify-end gap-2 align-middle">
                    <Icon name="plus" id="add" variant={IconVariants.LARGE} />
                    <Typography className={`'text-base-500' `}> {option.name} </Typography>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <>
            <div className="text-center">
              <Typography className="px-4 py-2 text-base-500">No sorting options available</Typography>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SortComponent;
